import { createContext, JSX, ReactNode, useContext } from 'react';
import * as vp from 'vopay';
import { useClientData } from './ClientData.provider';

export type LockedFeaturesCtx = {
  GCMEnabled?: boolean;
  EFTCollectEnabled?: boolean;
  EFTSendEnabled?: boolean;
  ACHFundEnabled?: boolean;
  ACHWithdrawEnabled?: boolean;
  USDEFTCollectEnabled?: boolean;
  USDEFTSendEnabled?: boolean;
  RTRWithdrawEnabled?: boolean;
  RTPWithdrawEnabled?: boolean;
  FedwireWithdrawEnabled?: boolean;
  VisaDirectEnabled?: boolean;
  InteracMoneyRequestEnabled?: boolean;
  InboundEtransferEnabled?: boolean;
  InteracBulkPayoutEnabled?: boolean;
  PortalViewOnly?: boolean;
  MoneyRequestGenerateUrlEnabled?: boolean;
  KYCLightEnabled?: boolean;
  SubAccountPermissions?: boolean;
  ClientAccountsEnabled?: boolean;
  AutoBalanceTransferEnabled?: boolean;
  TransactionLabelOverrideEnabled?: boolean;
  IQ11StandardEnabled?: boolean;
  IQ11BoostEnabled?: boolean;
  IQ11PlusEnabled?: boolean;
  IQ11AdvantageEnabled?: boolean;
  TransactionServiceDefault?: boolean;
  PayLinkEnabled?: boolean;
  CustomFlinksCredentialsSet?: boolean;
  CustomPlaidCredentialsSet?: boolean;
  CustomInveriteCredentialsSet?: boolean;
  EnablePaylinkDecline?: boolean;
  CreditCardEnabled?: boolean;
  GooglePayEnabled?: boolean;
  UserManagementEnabled?: boolean;
  AccountingSoftwareIntegrationEnabled?: boolean;
  AccountingSystemIntegrationEnabled?: boolean;
  AccountTransferEnabled?: boolean;
  PushToCardEnabled?: boolean;
  RealTimeMoneyRequestEnabled?: boolean;
  CustomGlobalPaySharedSecret?: boolean;
  LockedSelectOptions?: { [key: string]: string };
  BrandingEmailEnabled?: boolean;
  BrandingELinxEnabled?: boolean;
  BrandingPortalEnabled?: boolean;
  BrandingOnboardingEnabled?: boolean;
  TransactionApprovalWorkflowEnabled?: boolean;
  EmailDomainEnabled?: boolean;
  ChatSupportEnabled?: boolean;
  InterestAccountEnabled?: boolean;
  BankAccountVerificationEnabled?: boolean;
  InteracVerificationEnabled?: boolean;
  BillPayEnabled?: boolean;
  AccountAndBillingEnabled?: boolean;
  FundMyAccountEnabled?: boolean;
  WithdrawMyAccountEnabled?: boolean;
  BankAccountTransferEnabled?: boolean;
  OperationalAccountsEnabled?: boolean;
  PartnerBillsAccount?: boolean;
  PaymentsEnabled?: boolean;
  CollectionsEnabled?: boolean;
  ShowSubAccountOrgChart?: boolean;
  InteracBasicVerificationEnabled?: boolean;
  InteracEnhancedVerificationEnabled?: boolean;
  MicroDepositBankAccountVerificationEnabled?: boolean;
  CardBasicVerificationEnabled?: boolean;
  CardEnhancedVerificationEnabled?: boolean;
  SupportTicketEnabled?: boolean;
  DigitalWalletsEnabled?: boolean;
  FileConversionEnabled?: boolean;
  FileSubmissionEnabled?: boolean;
};

export type LockedType = {
  id: string;
  name: string;
  locked: boolean;
};

export const LockedFeaturesContext = createContext<LockedFeaturesCtx>({});

export const LockedFeaturesProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { response } = useClientData();

  const values: LockedFeaturesCtx = {
    GCMEnabled: vp.toBool(response.GCMEnabled),
    EFTCollectEnabled: vp.toBool(response.EFTCollectEnabled),
    EFTSendEnabled: vp.toBool(response.EFTSendEnabled),
    ACHFundEnabled: vp.toBool(response.ACHFundEnabled),
    ACHWithdrawEnabled: vp.toBool(response.ACHWithdrawEnabled),
    USDEFTCollectEnabled: vp.toBool(response.USDEFTCollectEnabled),
    USDEFTSendEnabled: vp.toBool(response.USDEFTSendEnabled),
    RTRWithdrawEnabled: vp.toBool(response.RTRWithdrawEnabled),
    RTPWithdrawEnabled: vp.toBool(response.RTPWithdrawEnabled),
    FedwireWithdrawEnabled: vp.toBool(response.FedwireWithdrawEnabled),
    VisaDirectEnabled: vp.toBool(response.VisaDirectEnabled),
    InteracMoneyRequestEnabled: vp.toBool(response.InteracMoneyRequestEnabled),
    InboundEtransferEnabled: vp.toBool(response.InboundEtransferEnabled),
    InteracBulkPayoutEnabled: vp.toBool(response.InteracBulkPayoutEnabled),
    PortalViewOnly: vp.toBool(response.PortalViewOnly),
    MoneyRequestGenerateUrlEnabled: vp.toBool(response.MoneyRequestGenerateUrlEnabled),
    KYCLightEnabled: vp.toBool(response.KYCLightEnabled),
    SubAccountPermissions: vp.toBool(response.SubAccountPermissions),
    ClientAccountsEnabled: vp.toBool(response.ClientAccountsEnabled),
    AutoBalanceTransferEnabled: vp.toBool(response.AutoBalanceTransferEnabled),
    TransactionLabelOverrideEnabled: vp.toBool(response.TransactionLabelOverrideEnabled),
    IQ11StandardEnabled: vp.toBool(response.IQ11StandardEnabled),
    IQ11BoostEnabled: vp.toBool(response.IQ11BoostEnabled),
    IQ11PlusEnabled: vp.toBool(response.IQ11PlusEnabled),
    IQ11AdvantageEnabled: vp.toBool(response.IQ11AdvantageEnabled),
    TransactionServiceDefault: vp.toBool(response.TransactionServiceDefault),
    PayLinkEnabled: vp.toBool(response.PayLinkEnabled),
    CustomFlinksCredentialsSet: response.CustomFlinksCredentialsSet,
    CustomPlaidCredentialsSet: response.CustomPlaidCredentialsSet,
    CustomInveriteCredentialsSet: response.CustomInveriteCredentialsSet,
    EnablePaylinkDecline: vp.toBool(response.EnablePaylinkDecline),
    CreditCardEnabled: vp.toBool(response.CreditCardEnabled),
    GooglePayEnabled: response.GooglePayEnabled,
    UserManagementEnabled: vp.toBool(response.UserManagementEnabled),
    AccountingSoftwareIntegrationEnabled: response.AccountingSoftwareIntegrationEnabled,
    AccountingSystemIntegrationEnabled: vp.toBool(response.AccountingSystemIntegrationEnabled),
    AccountTransferEnabled: vp.toBool(response.AccountTransferEnabled),
    PushToCardEnabled: vp.toBool(response.PushToCardEnabled),
    RealTimeMoneyRequestEnabled: vp.toBool(response.RealTimeMoneyRequestEnabled),
    CustomGlobalPaySharedSecret: response.CustomGlobalPaySharedSecret,
    LockedSelectOptions: createLockedSelectOptions(),
    BrandingEmailEnabled: vp.toBool(response.BrandingEmailEnabled),
    BrandingELinxEnabled: vp.toBool(response.BrandingELinxEnabled),
    BrandingPortalEnabled: vp.toBool(response.BrandingPortalEnabled),
    BrandingOnboardingEnabled: vp.toBool(response.BrandingOnboardingEnabled),
    TransactionApprovalWorkflowEnabled: vp.toBool(response.TransactionApprovalWorkflowEnabled),
    EmailDomainEnabled: vp.toBool(response.EmailDomainEnabled),
    InterestAccountEnabled: response.InterestAccountEnabled,
    ChatSupportEnabled: response.ChatSupportEnabled,
    BankAccountVerificationEnabled: response.BankAccountVerificationEnabled,
    InteracVerificationEnabled: response.InteracVerificationEnabled,
    InteracBasicVerificationEnabled: response.InteracBasicVerificationEnabled,
    InteracEnhancedVerificationEnabled: response.InteracEnhancedVerificationEnabled,
    BillPayEnabled: response.BillPayEnabled,
    AccountAndBillingEnabled: response.AccountAndBillingEnabled,
    FundMyAccountEnabled: response.FundMyAccountEnabled,
    WithdrawMyAccountEnabled: response.WithdrawMyAccountEnabled,
    BankAccountTransferEnabled: response.BankAccountTransferEnabled,
    OperationalAccountsEnabled: response.OperationalAccountsEnabled,
    PartnerBillsAccount: response.PartnerBillsAccount,
    PaymentsEnabled: response.PaymentsEnabled,
    CollectionsEnabled: response.CollectionsEnabled,
    ShowSubAccountOrgChart: response.ShowSubAccountOrgChart,
    MicroDepositBankAccountVerificationEnabled: response.MicroDepositBankAccountVerificationEnabled,
    CardBasicVerificationEnabled: response.CardBasicVerificationEnabled,
    CardEnhancedVerificationEnabled: response.CardEnhancedVerificationEnabled,
    FileConversionEnabled: response.FileConversionEnabled,
    FileSubmissionEnabled: response.FileSubmissionEnabled,
    SupportTicketEnabled: response.SupportTicketEnabled,
    DigitalWalletsEnabled: response.DigitalWalletsEnabled,
  };

  function createLockedSelectOptions(): { [key: string]: string } {
    const lockedPaymentRails: { [key: string]: LockedType } = {
      creditCard: {
        id: 'Credit Card',
        name: 'Credit Card',
        locked: !vp.toBool(response.CreditCardEnabled),
      },
      debittCard: {
        id: 'Debit Card',
        name: 'Debit Card',
        locked: !vp.toBool(response.VisaDirectEnabled) && !vp.toBool(response.PushToCardEnabled),
      },
      email: {
        id: 'Email',
        name: 'Email',
        locked:
          !vp.toBool(response.RealTimeMoneyRequestEnabled) &&
          !vp.toBool(response.InteracMoneyRequestEnabled) &&
          !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      interacEtransferMoneyRequest: {
        id: 'Interac e-Transfer Money Request',
        name: '<i>Interac</i> Money Request<sup>&reg;</sup>',
        locked: !vp.toBool(response.InteracMoneyRequestEnabled),
      },
      interacEtransfer: {
        id: 'Interac e-Transfer',
        name: 'Interac e-Transfer',
        locked: !vp.toBool(response.RealTimeMoneyRequestEnabled) && !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      interac: {
        id: 'interac',
        name: 'Interac',
        locked: !vp.toBool(response.InteracMoneyRequestEnabled) && !vp.toBool(response.InteracBulkPayoutEnabled),
      },
      rtp: {
        id: 'RTP',
        name: 'RTP',
        locked: !vp.toBool(response.RTPWithdrawEnabled),
      },
      fedwire: {
        id: 'Fedwire',
        name: 'Fedwire',
        locked: !vp.toBool(response.FedwireWithdrawEnabled),
      },
      eft: {
        id: 'eft',
        name: 'EFT',
        locked: false,
      },
      bankAccount: {
        id: 'Bank Account',
        name: 'Bank Account',
        locked:
          !vp.toBool(response.EFTCollectEnabled) &&
          !vp.toBool(response.EFTSendEnabled) &&
          !vp.toBool(response.ACHFundEnabled) &&
          !vp.toBool(response.ACHWithdrawEnabled),
      },
      voPayInstant: {
        id: 'VoPay Instant',
        name: 'VoPay Instant',
        locked: !vp.toBool(response.RTRWithdrawEnabled),
      },
      usd: {
        id: 'USD',
        name: 'USD',
        locked:
          !vp.toBool(response.USDEFTCollectEnabled) &&
          !vp.toBool(response.USDEFTSendEnabled) &&
          !vp.toBool(response.ACHFundEnabled) &&
          !vp.toBool(response.ACHWithdrawEnabled),
      },
      cad: {
        id: 'CAD',
        name: 'CAD',
        locked: false,
      },
      paylink: {
        id: 'Paylink',
        name: 'I would like to request the Bank Account details from my Contact',
        locked: !vp.toBool(response.PayLinkEnabled),
      },
      googlePay: {
        id: 'Google Pay',
        name: 'Google Pay',
        locked: !response.GooglePayEnabled,
      },
      paypal: {
        id: 'PayPal',
        name: 'PayPal',
        locked: !response.DigitalWalletsEnabled,
      },
      venmo: {
        id: 'Venmo',
        name: 'Venmo',
        locked: !response.DigitalWalletsEnabled,
      },
      bankVerification: {
        id: 'Bank Account Verification',
        name: 'Bank Account Verification',
        locked: !response.BankAccountVerificationEnabled,
      },
      interacVerification: {
        id: 'Interac Verification',
        name: 'Interac Verification',
        locked: !response.InteracVerificationEnabled,
      },
      interacBasicVerification: {
        id: 'Interac Basic Verification',
        name: 'Interac Basic Verification',
        locked: !response.InteracBasicVerificationEnabled,
      },
      interacEnhancedVerification: {
        id: 'Interac Enhanced Verification',
        name: 'Interac Enhanced Verification',
        locked: !response.InteracEnhancedVerificationEnabled,
      },
    };

    return Object.values(lockedPaymentRails).reduce((filtered: { [key: string]: string }, option: LockedType) => {
      if (option.locked) {
        filtered[option.id] = option.name;
      }
      return filtered;
    }, {});
  }

  return <LockedFeaturesContext.Provider value={values}>{children}</LockedFeaturesContext.Provider>;
};

export const useLockedFeatures = (): LockedFeaturesCtx => useContext(LockedFeaturesContext);
